// File: `typescript/apps/front-end/src/main/react/user/UserButtons.tsx`
import React, {useMemo, useState} from "react";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import {AjaxGroupRestControllerApi, AjaxUserRestControllerApi, RejectGroupRequestRequest} from "@/generated-client";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import {ClientFactory} from "@/main/common/ClientFactory";
import {
    emitGroupJoinRequestRemoved,
    emitGroupJoinRequestSubmitted,
    emitGroupMemberAdded,
    emitGroupMemberRemoved,
    emitOpenChatDrawer,
    emitUserFollowAdded,
    emitUserFollowRemoved,
    onUserFollowAdded,
    onUserFollowRemoved,
} from "@/main/common/EventRegistry";
import {MetaHandler} from "@/main/common/MetaHandler";
import {useViewerRelationshipState} from "@/main/common/ViewerActionRecords";
import {buildUserSafetyDropdownItems, useUserSafetyActions} from "@/main/react/user/useUserSafetyActions";
import {CtaButton, CtaButtonDropdown, CtaToggle, DropdownItem, DropdownSection} from "@/main/react/buttons/CtaButton";
import {EyeOff, Flag, MessagesSquare, MoreHorizontal, Share, Tag, UserMinus, UserPlus, UserPlus2, UserX} from "lucide-react";
import {Localizer} from "@/main/react/Types";
import {showBanUserDialog} from "@/main/react/Dialog/GroupBanUserDialogLazy";
import {showInviteUserToGroupsDialog} from "@/main/react/Dialog/InviteUserToGroupsDialogLazy";
import {showReportUserDialog} from "@/main/react/Dialog/ReportUserDialogLazy";
import {showSetFlairDialog} from "@/main/react/group/SetFlairDialogLazy";
import {showRejectJoinRequestDialog} from "@/main/react/Dialog/RejectJoinRequestDialog";
import {showIgnoredUndoToast} from "@/main/react/Dialog/ignoredUndoToast";
import {showYesNoDialog} from "@/main/react/Dialog/YesNoDialog";
import {toast} from "sonner";
import {shareUrl} from "@/main/util/PlatformIdentifierUtil";
import logger from "@/lib/logger";
import {cn} from "@/lib/utils";
import {trackFollowAdded, trackFollowRemoved, trackFollowStarted, trackShare, type AnalyticsSurface} from "@/main/analytics/analyticsEvents";
import {X} from "lucide-react";
import {ACCOUNT_ACCESS_DIALOG_COPY, withAccountAccessSurface} from "@/main/react/account_access/AccountAccessDialogContext";

/**
 * Unified buttons for user profile, group member cards and join requests.
 */
export type Props = {
    username: string;
    initiallyFollowing?: boolean;
    groupPublicId?: string;
    initiallyBanned?: boolean;
    initiallyViewerIsLeader?: boolean;
    requestPublicId?: string;
    requestStatus?: string;
    localizer: Localizer;
    allowWrap?: boolean;
    displayMode?: "full" | "primary-only";
    /**
     * Where the action row sits inside the slot its parent gives it. The PARENT owns this — the row is
     * shared by the profile header, group member rows, join-request rows, `HomeDiscoveryRail` and
     * `UserCardDisplayer`, and only the profile rail wants it left-aligned (ux-refine-user decision ①).
     *
     * `"end"` is the default precisely so that not passing it is the pre-existing behaviour: every
     * consumer except `UserHeader`'s rail branch is untouched. Note this governs TWO class mechanisms —
     * `ml-auto` (where the wrapper itself sits in its flex parent) and `justify-*` (how the buttons
     * pack inside it) — because the rail's wrapper is `flex w-full` and either one alone still pins the
     * row to the right edge.
     */
    align?: "start" | "end";
    /**
     * Render this row's own primary action in a quieter tier (`outline` rather than `primary`).
     *
     * <p>Presentational, opt-in, and set by the HOST — this component never infers it. It is the same
     * prop, with the same name and the same reasoning, that `GroupButtons` already carries for
     * `DASHBOARD-12`: a list whose every row draws an identical filled-primary action out-shouts the
     * thing the page is actually for.
     *
     * <p><b>`GROUP-20` is the case it exists for.</b> On a group's Members section an anonymous
     * visitor met four filled Follow buttons, each 81x32px down the widest column, against a 68x32px
     * filled Join in the rail — roughly 5:1 of primary-blue area in favour of four actions about
     * individual strangers over the one action that changes the visitor's relationship to the group.
     * Operator decision B12 forbids fixing that by demoting Join, so the per-row Follow is demoted
     * instead, at that one call site.
     *
     * <p><b>Why a prop and not a rule in here.</b> The profile header passes no `displayMode` and
     * keeps its filled Follow — that is B12's recorded constraint and the sibling case in
     * `UserButtons.test.tsx` defends it. The analytics `surface` prop is not a presentation signal
     * either; re-purposing it would be the same mistake spelled differently.
     */
    demotePrimaryAction?: boolean;
    /** Where this button is mounted, for follow/unfollow attribution. See the join/follow analytics design. */
    surface?: AnalyticsSurface;
    /**
     * The member's flair in this group, and where a saved change goes (round 7 slice 2b, D20). "Set flair" is
     * offered only when the host passes `onFlairChange`, so a surface that cannot show a flair never offers one.
     */
    flair?: string | null;
    onFlairChange?: (flair: string | null) => void;
    /**
     * Whether THIS viewer may start a conversation with this member (safety plan Phase 1a, round 25
     * D16), from `UserProfileDTO.canMessage` / `UserPageBootstrapDTO.canMessage`. Only an explicit
     * `false` hides the Message button; `undefined` and `null` mean "not asked for" (every list and search
     * surface, where the backend leaves it null) and keep today's behaviour. See the predicate below.
     */
    canMessage?: boolean | null;
};

export function UserButtons(props: Props) {
    const {
        username,
        initiallyFollowing = false,
        groupPublicId = "",
        initiallyBanned = false,
        initiallyViewerIsLeader = false,
        requestPublicId = "",
        requestStatus: initialRequestStatus = "",
        localizer,
        allowWrap = true,
        displayMode = "full",
        align = "end",
        demotePrimaryAction = false,
        surface = "page",
        flair = null,
        onFlairChange,
    } = props;

    const userApi: AjaxUserRestControllerApi = useMemo(() => ClientFactory.getAjaxUserRestControllerApi(), []);
    const groupApi: AjaxGroupRestControllerApi = useMemo(() => ClientFactory.getAjaxGroupRestControllerApi(), []);

    // Not plain useState: discovery cards unmount when their section collapses, and `initiallyFollowing`
    // is a stale page-bootstrap value on remount. The hook also owns the prop resync that used to live
    // in the effect below, so a re-supplied stale prop can no longer clobber a real follow.
    const [isFollowing, setIsFollowing] = useViewerRelationshipState("user-follow", username, initiallyFollowing);
    const [isBanned, setIsBanned] = useState<boolean>(initiallyBanned);
    const [busy, setBusy] = useState(false);
    const [requestStatus, setRequestStatus] = useState<string>(initialRequestStatus);
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    // The safety half of this component lives in `useUserSafetyActions`, shared with the comment and
    // discussion overflow menus (design 2026-08-10, slice A). It fetches on mount here — unlike a content
    // menu, which fetches on open — because the follow and message controls below are rendered from
    // `blockedByViewer` / `viewerBlockedByUser` before any menu is touched.
    // D1: whether this row can offer a group ban at all. Computed from props and the meta layer only —
    // NOT from `safety.isSelf` — because it is handed to the hook below, and reading it back out of the
    // hook's own result would be circular. Same predicate the hook uses for `isSelf`, one line earlier.
    const viewerIsTarget = Boolean(loggedInUsername && username && loggedInUsername === username);
    const canBanFromThisGroup = Boolean(groupPublicId) && initiallyViewerIsLeader && !viewerIsTarget;

    /**
     * D1: a leader who has just blocked someone still has them in the group. Offer the ban instead of
     * making the leader go and do the same work again on another screen.
     *
     * A toast with an action rather than a second confirm dialog: a leader may well have blocked this
     * person for a reason that has nothing to do with the group, and must be able to ignore the
     * suggestion without dismissing anything. Block stays personal (D1) — nothing here bans anybody; it
     * opens the same dialog, against the same endpoint, under the same server-side leadership check.
     */
    const suggestGroupBan = React.useCallback((blockedUsername: string) => {
        toast.info(localizer("groups.ban.suggest_after_block", blockedUsername), {
            duration: 10000,
            action: {
                // The label carries the stable hook rather than the toast: sonner renders it inside its
                // own action button, so a spec can find and click it without depending on the localized
                // word, which a copy rename would silently break (rule 20).
                label: <span data-playwright-id="group-ban-suggestion-action">{localizer("groups.ban")}</span>,
                onClick: () => {
                    showBanUserDialog(groupPublicId, localizer, blockedUsername, () => {
                        setIsBanned(true);
                        toast.success(localizer("groups.modal.ban.success"));
                    });
                },
            },
        });
    }, [groupPublicId, localizer]);

    const safety = useUserSafetyActions({
        username,
        localizer,
        externallyBusy: busy,
        // Undefined on every content menu and on any non-group surface, so the branch does not exist
        // there at all. The hook itself stays group-unaware.
        onBlocked: canBanFromThisGroup ? suggestGroupBan : undefined,
    });
    const {blockedByViewer, viewerBlockedByUser, isSelf} = safety;
    // One busy flag across every verb on this row, as before the extraction: the hook owns the block/mute
    // half and is handed this component's own `busy`, so neither can start while the other is in flight.
    const anyBusy = busy || safety.busy;
    const accountAreaAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.accountArea, "user_account_area");
    const followAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.follow, "user_follow");
    const messagesAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.messages, "user_message");
    const reportAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.report, "user_report");
    const safetyAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.safety, "user_safety");

    React.useEffect(() => {
        if (!username || !loggedInUsername) {
            return;
        }

        const unsubscribeAdded = onUserFollowAdded((payload) => {
            if (!payload || payload.targetUsername !== username || payload.actorUsername !== loggedInUsername) {
                return;
            }
            setIsFollowing(true);
        });

        const unsubscribeRemoved = onUserFollowRemoved((payload) => {
            if (!payload || payload.targetUsername !== username || payload.actorUsername !== loggedInUsername) {
                return;
            }
            setIsFollowing(false);
        });

        return () => {
            unsubscribeAdded();
            unsubscribeRemoved();
        };
    }, [loggedInUsername, username]);

    const toggleFollow = async () => {
        if (anyBusy) return;



        if (isSelf) {
            toast.warning(localizer("typescript.user.follow.self"), {
                position: "top-right",
                duration: 5000,

            });
            return;
        }

        if (blockedByViewer || viewerBlockedByUser) {
            return;
        }

        await writeFollow(!isFollowing);
    };

    /**
     * Writes an explicit follow TARGET, never a flip, so the Undo on the unfollow toast (QD-3) re-runs
     * exactly the follow branch (`emitUserFollowAdded`, `trackFollowAdded`) and counts stay in sync
     * whatever the button reads by the time the member presses it.
     */
    const writeFollow = async (follow: boolean) => {
        setBusy(true);
        setIsFollowing(follow); // optimistic
        try {
            if (!follow) {
                await userApi.unfollowUser(String(username), surface);
                if (loggedInUsername) {
                    emitUserFollowRemoved({
                        targetUsername: String(username),
                        actorUsername: loggedInUsername,
                    });
                }
                trackFollowRemoved("user", surface);
                toast.success(localizer("typescript.user.unfollow.success"), {
                    action: {
                        // The stable hook rides inside the label (the unsave-undo precedent).
                        label: <span data-playwright-id="user-unfollow-undo-action">{localizer("literal.undo")}</span>,
                        onClick: () => { void writeFollow(true); },
                    },
                });
            } else {
                // Intent event fires before the mutation so click->success conversion is measurable.
                trackFollowStarted("user", surface);
                await userApi.followUser(String(username), surface);
                if (loggedInUsername) {
                    emitUserFollowAdded({
                        targetUsername: String(username),
                        actorUsername: loggedInUsername,
                        actor: {
                            username: loggedInUsername,
                        },
                        target: {
                            username: String(username),
                            isViewerFollowing: true,
                        },
                    });
                }
                trackFollowAdded("user", surface);
                toast.success(localizer("typescript.user.follow.success"));
            }
        } catch (err) {
            setIsFollowing(!follow);
            if (isHandledAuthSessionError(err)) {
                return;
            }
            logger.error("Follow/unfollow failed", { err });
            toast.error(localizer("typescript.user.follow.update_failed"));
        } finally {
            setBusy(false);
        }
    };

    const handleBan = async () => {
        // Defence in depth beside the render guard on `mainIsBan`. A render guard is one layer, and
        // this handler is also the target of the moderation-menu wiring, so the predicate lives on both
        // sides rather than only on whichever one a future edit happens to keep.
        if (isSelf) return;
        if (!initiallyViewerIsLeader) return;
        if (!groupPublicId) return;
        if (anyBusy) return;
        if (!username) {
            toast.error(localizer("groups.modal.ban.invalid_username"));
            return;
        }
        showBanUserDialog(groupPublicId, localizer, username, () => {
            setIsBanned(true);
            toast.success(localizer("groups.modal.ban.success"));
        });
    };

    const handleUnban = async () => {
        if (!initiallyViewerIsLeader) return;
        if (anyBusy) return;
        if (!username) {
            toast.error(localizer("groups.modal.unban.invalid_username"));
            return;
        }

        showYesNoDialog({
            localizer,
            title: localizer("groups.unban"),
            message: <div>{localizeWithMemberName(localizer, "groups.modal.unban.confirm", username)}</div>,
            yesLabel: localizer("groups.unban"),
            yesIcon: UserPlus,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            onYes: async () => {
                setBusy(true);
                const prev = isBanned;
                setIsBanned(false); // optimistic

                try {
                    await groupApi.unbanUser(groupPublicId, String(username));
                    toast.success(localizer("groups.modal.unban.success"));
                } catch (err) {
                    logger.error("Unban failed", { err });
                    setIsBanned(prev); // rollback
                    // YesNoDialog surfaces the failure banner on re-throw; don't also toast.
                    throw err;
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    // P1-A: eject a member without banning them. Distinct from Ban on purpose — no ban row is written,
    // the person is not notified, and they can rejoin or re-request straight away. The confirm copy says
    // so, because "remove" and "ban" are otherwise easy to confuse.
    const handleRemoveFromGroup = async () => {
        if (!initiallyViewerIsLeader) return;
        if (anyBusy) return;
        if (!username || !groupPublicId) {
            toast.error(localizer("groups.modal.ban.invalid_username"));
            return;
        }

        showYesNoDialog({
            localizer,
            title: localizer("group.member.remove.action"),
            message: <div>{localizeWithMemberName(localizer, "group.member.remove.confirm", String(username))}</div>,
            yesLabel: localizer("group.member.remove.action"),
            yesIcon: UserX,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            onYes: async () => {
                setBusy(true);
                try {
                    await ClientFactory.removeGroupMember(groupPublicId, String(username));
                    // Optimistic list updates ride the existing event bus: the group page, user page,
                    // dashboard and group cards all already subscribe to groupMemberRemoved.
                    emitGroupMemberRemoved({
                        groupPublicId,
                        username: String(username),
                        source: "removed",
                    });
                    toast.success(localizer("group.member.remove.success"));
                } catch (err) {
                    logger.error("Remove member failed", { err });
                    // YesNoDialog surfaces the failure banner on re-throw; don't also toast.
                    throw err;
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    const handleApprove = async () => {
        if (!initiallyViewerIsLeader) return;
        if (anyBusy) return;

        setBusy(true);
        const prev = requestStatus;
        setRequestStatus("APPROVED"); // optimistic UI change

        try {
            await groupApi.approveJoinRequest(String(requestPublicId));
            if (groupPublicId) {
                emitGroupJoinRequestRemoved({
                    groupPublicId,
                    requestPublicId: String(requestPublicId),
                    username: String(username),
                    resolution: "approved",
                });
                emitGroupMemberAdded({
                    groupPublicId,
                    member: {
                        username: String(username),
                        groupPublicId,
                    },
                    source: "requestApproved",
                });
            }
            toast.success(localizer("groups.request.approve.success"));
        } catch (err) {
            logger.error("Approve request failed", { err });
            setRequestStatus(prev); // rollback
            toast.error(localizer("groups.request.approve.failed"));
        } finally {
            setBusy(false);
        }
    };

    const handleReject = async () => {
        if (!initiallyViewerIsLeader) return;
        if (anyBusy) return;
        if (!requestPublicId) {
            toast.error(localizer("groups.request.invalid_request_id"));
            return;
        }

        showRejectJoinRequestDialog({
            localizer,
            username,
            onConfirm: async (resolutionNote?: string) => {
                setBusy(true);
                const prev = requestStatus;
                setRequestStatus("REJECTED");

                try {
                    const body: RejectGroupRequestRequest = {resolutionNote: resolutionNote ?? undefined};
                    await groupApi.rejectJoinRequest(String(requestPublicId), body);
                    if (groupPublicId) {
                        emitGroupJoinRequestRemoved({
                            groupPublicId,
                            requestPublicId: String(requestPublicId),
                            username: String(username),
                            resolution: "rejected",
                        });
                    }
                    toast.success(localizer("groups.request.reject.success"));
                } catch (err) {
                    logger.error("Reject request failed", { err });
                    setRequestStatus(prev); // rollback
                    toast.error(localizer("groups.request.reject.failed"));
                    throw err;
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    // SV-146 (D7): one click, no reason dialog; the asker is never told and still sees "pending". The
    // requests list drops the row on the removed event, and the toast's Undo re-announces the request so
    // the list refetches it and the queue badge counts it again.
    const handleIgnore = async () => {
        if (!initiallyViewerIsLeader) return;
        if (anyBusy) return;
        if (!requestPublicId) {
            toast.error(localizer("groups.request.invalid_request_id"));
            return;
        }

        setBusy(true);
        const prev = requestStatus;
        setRequestStatus("IGNORED");
        const ignoredRequestPublicId = String(requestPublicId);
        try {
            await groupApi.ignoreJoinRequest(ignoredRequestPublicId);
            if (groupPublicId) {
                emitGroupJoinRequestRemoved({
                    groupPublicId,
                    requestPublicId: ignoredRequestPublicId,
                    username: String(username),
                    resolution: "ignored",
                });
            }
            showIgnoredUndoToast({
                localizer,
                message: "groups.request.ignore.success",
                undo: () => groupApi.undoIgnoreJoinRequest(ignoredRequestPublicId),
                onUndone: () => {
                    setRequestStatus("PENDING");
                    if (groupPublicId) {
                        emitGroupJoinRequestSubmitted({ groupPublicId, username: String(username) });
                    }
                },
            });
        } catch (err) {
            logger.error("Ignore request failed", { err });
            setRequestStatus(prev); // rollback
            toast.error(localizer("groups.request.ignore.failed"));
        } finally {
            setBusy(false);
        }
    };

    const handleOpenChat = () => {
        if (anyBusy || isSelf || !username || blockedByViewer || viewerBlockedByUser) {
            return;
        }
        emitOpenChatDrawer({
            targetUsername: String(username),
            focusComposer: true,
        });
    };

    const isRequest = Boolean(requestPublicId);
    const isMember = Boolean(groupPublicId) && !isRequest;
    const showRequestButtons = isRequest && initiallyViewerIsLeader && requestStatus !== "APPROVED" && requestStatus !== "REJECTED" && requestStatus !== "IGNORED";
    // A leader's own member row is not a moderation target. This predicate — not `mainIsBan` — is the
    // right place for that: `mainIsX` means "X owns the MAIN slot", and every moderation control has a
    // menu fallback gated on `!mainIsX`, so suppressing Ban there RELOCATES it into the overflow menu
    // instead of removing it. (Tried first, on 2026-08-03; the regression test below caught the
    // `dropdown-item-ban-toggle` that appeared in its place.)
    const showMemberBanButtons = isMember && initiallyViewerIsLeader && !isSelf;
    const isEitherDirectionBlocked = blockedByViewer || viewerBlockedByUser;
    // D16: the member's DM setting excludes this viewer (no conversation yet, and the setting does not
    // admit a new one). Only an explicit `false` hides the button; `null` and `undefined` are "not
    // asked for" and keep it.
    //
    // <p>The viewer term this used to carry (`&& Boolean(loggedInUsername)`) is GONE, deliberately
    // (round-26 D1 / A8, answering the parked `r25-w2-dm-ui-D16-anon`). It existed because the backend
    // answered `false` for an anonymous viewer, which would have deleted the sign-up prompt this button
    // is for a signed-out visitor. The backend now answers `null` — "no opinion about someone with no
    // account" — so the anonymous visitor keeps the button for the same reason as before, expressed once,
    // on the side that actually knows the policy. Carrying both would mean a permission that two files
    // have to agree about. The send-time refusal in ChatService stays the backstop for the rest.
    //
    // ⚠️ This is only correct with the backend half merged; the two land together.
    const excludedByDirectMessagePolicy = props.canMessage === false;
    const showMessageButton = displayMode !== "primary-only" && !isSelf && !isEitherDirectionBlocked
        && !excludedByDirectMessagePolicy;

    // Which action owns the MAIN slot. Deliberately still free of any `isSelf` term — see
    // `showMemberBanButtons` above; these also drive the `!mainIsX` menu fallbacks, so a self check
    // here would move a control rather than remove it.
    const mainIsFollow = !showRequestButtons && !showMemberBanButtons;
    const mainIsBan = showMemberBanButtons;
    const mainIsRequest = showRequestButtons;
    // `LANDING-48` / `HOME-FEED-20` — the third file of `DISCUSSION-5` operator decision A.
    //
    // That decision reserved filled primary for a page's own main action and demoted rail join/follow
    // to outline. It landed in `GroupButtons` (`isRailPresentation`) and `SeriesButtons` on 2026-08-21
    // and never reached here, so one discovery rail drew three actions of identical commitment at two
    // emphasis levels: outline Join, outline Follow, and a saturated filled Follow about 300px apart.
    // Both swept files carry the warning in as many words — the three must move in lockstep "or an
    // outline Follow lands beside a filled Join inside one rail".
    //
    // Keyed on `displayMode`, not on the component: the profile page's own header passes no
    // `displayMode` and keeps its filled Follow, which is the constraint `DISCUSSION-5` recorded. The
    // two call sites this fixes are `HomeDiscoveryRail`'s `ActivePublisherCard` and `WelcomeOverlay`.
    //
    // `GROUP-20` adds the third demotion axis, `demotePrimaryAction`: a host that knows its own page
    // owns a louder action than this row does. See the prop docs above for why it is opt-in.
    const followVariant = (isFollowing || displayMode === "primary-only" || demotePrimaryAction) ? "outline" : "primary";
    const followActiveClass = isFollowing
        ? "!border-[var(--line-color)] !bg-[var(--card)] !text-[var(--text-main)] !stroke-[var(--text-main)]"
        : undefined;

    const generalItems: DropdownItem[] = [];
    const moderationItems: DropdownItem[] = [];

    // `!isSelf` is redundant today (a self card always takes the `mainIsFollow` branch, which suppresses
    // this fallback) and is stated anyway: the redundancy is what keeps it correct if the slot logic
    // above ever changes, and the whole point of this fix is that a control removed from one slot must
    // not reappear in the other.
    if (!mainIsFollow && !isEitherDirectionBlocked && !isSelf) {
        generalItems.push({
            id: "follow-toggle",
            icon: isFollowing ? UserMinus : UserPlus,
            text: isFollowing ? (localizer("user.unfollow")) : (localizer("user.follow")),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void toggleFollow();
            },
            requiresLogin: true,
            accountAccessDialog: followAccountAccessDialog,
        });
    }

    generalItems.push({
        id: "share",
        icon: Share,
        text: localizer("literal.share"),
        requiresLogin: false,
        onClick: async (ev?: Event) => {
            try {
                ev?.preventDefault();
                ev?.stopPropagation();
            } catch {
            }
            try {
                trackShare("user", "user_actions");
                const sharePath = `/user/${username}`;
                const shareFull = (typeof window !== 'undefined' ? window.location.origin : '') + sharePath;
                // entityId is the username: a profile has no separate public id, and this matches how
                // FOLLOW_ADDED already records USER entities.
                await shareUrl(shareFull, localizer("literal.share"), localizer,
                    { entityType: "USER", entityId: username, entityTitle: username });
            } catch (err) {
                logger.error("Share failed", { err });
                // Was `group.share.failed` — a group-namespaced key rendering on a profile. Same visible
                // sentence today, but the two would drift the moment either is edited for its own surface.
                toast.error(localizer("share_url.share_failed"));
            }
        },
    });

    /**
     * "Invite to group" — the person-first entry into the group picker.
     *
     * <p><strong>This one push ships the item on FIVE surfaces</strong>, not the two the design was
     * asked for: `UserButtons` is the shared action row for the profile header, `UserCardDisplayer`,
     * `HomeDiscoveryRail`, group member rows and group join-request rows. That is the intended outcome —
     * "this person belongs in one of my groups" is the same thought on a discovery card as on a
     * join-request row, where a leader looking at someone's request to join THIS group has an obvious use
     * for inviting them to a sibling one (design §3).</p>
     *
     * <p>In `generalItems` immediately after `share`, never in `moderationItems`: it is not a moderation
     * action and must not sit in the section that ends in Report / Mute / Block.</p>
     *
     * <p>Three cheap local conditions, no network call. It is deliberately NOT gated on "the viewer has
     * at least one group" — knowing that costs a fetch per rendered card, and `GroupGovernanceApi`'s own
     * header records what a per-mount viewer request cost last time (39% of one endpoint's prod traffic).
     * A viewer with no groups opens the dialog and gets the empty state, which offers Create a group.</p>
     *
     * <p>`requiresLogin` rather than a hidden item, so an anonymous visitor sees it and gets the sign-in
     * dialog instead of a menu that silently differs by session state.</p>
     */
    if (!isSelf && !isEitherDirectionBlocked) {
        generalItems.push({
            id: "invite-to-group",
            // `UserPlus2`, not `UserPlus`: that one is the follow control one line above in this same menu.
            icon: UserPlus2,
            text: localizer("user.invite_to_group"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                showInviteUserToGroupsDialog(String(username), localizer);
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });
    }

    // D1: gated on `canBanFromThisGroup`, NOT on `showMemberBanButtons`, and the difference is the
    // point. `showMemberBanButtons` decides who owns the MAIN button slot; widening it would have put
    // Ban and Approve/Reject in that one slot together on a join-request row. This predicate only asks
    // "may this viewer ban this person from this group", so Ban reaches the overflow menu on a request
    // row (the D3 surface) while a member row keeps it in the main slot exactly as before.
    if (canBanFromThisGroup && !mainIsBan) {
        moderationItems.push({
            id: "ban-toggle",
            icon: isBanned ? UserPlus : UserMinus,
            text: isBanned ? (localizer("groups.unban")) : (localizer("groups.ban")),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void (isBanned ? handleUnban() : handleBan());
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });
    }

    // P1-A: Ban remains the primary destructive action (rendered as the main toggle); Remove sits
    // beside it in the moderation menu as the quieter option. Hidden for an already-banned user, whose
    // membership is gone anyway, and never offered on your own card.
    if (showMemberBanButtons && !isBanned && !isSelf) {
        moderationItems.push({
            id: "remove-from-group",
            icon: UserX,
            text: localizer("group.member.remove.action"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void handleRemoveFromGroup();
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });
    }

    // Round 7 slice 2b (D20): the same rows as "Remove from group" (a leader viewer, a non-banned member, never
    // your own row), and only when the host can show the result. The server applies the finer MODERATOR+ and
    // `canManage` target rule; a refusal is the dialog's error line.
    if (showMemberBanButtons && !isBanned && !isSelf && onFlairChange) {
        moderationItems.push({
            id: "group-member-set-flair",
            icon: Tag,
            text: localizer("group.member.flair.action"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                showSetFlairDialog({
                    groupPublicId,
                    username: String(username),
                    initialFlair: flair,
                    localizer,
                    onSaved: onFlairChange,
                });
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });
    }

    if (!isSelf) {
        moderationItems.push({
            id: "report-user",
            icon: Flag,
            text: localizer("user.report"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                showReportUserDialog(localizer, username);
            },
            requiresLogin: true,
            accountAccessDialog: reportAccountAccessDialog,
        });

        // D29: block and mute are HIDDEN on the support account and on your own card, not merely left
        // inert — the predicate now lives in `useUserSafetyActions.canOfferSafetyControls`, which the
        // comment and discussion menus share, so the three surfaces cannot disagree about who may be
        // blocked. Reporting is deliberately NOT hidden on support: it does exactly what it says.
        moderationItems.push(...buildUserSafetyDropdownItems(safety, localizer, safetyAccountAccessDialog));
    }

    const dropdownSections: DropdownSection[] = [];
    if (generalItems.length > 0) dropdownSections.push({id: "general", title: null, items: generalItems});
    if (moderationItems.length > 0) dropdownSections.push({id: "moderation", title: null, items: moderationItems});

    /**
     * `DISCUSSION-13`, the residual half. Nine participant cards render an identical "Follow" and an
     * identical "Direct message", so out of reading order there is no way to tell which of nine people
     * you are about to follow (WCAG 2.2 4.1.2, repeated per row).
     *
     * <p>The recorded 2-key estimate for this — `user.action.follow.aria` / `user.action.message.aria`
     * with a MessageFormat argument, 2 x 25 catalogs — is NOT paid, because `aria-labelledby` composes
     * a name from REFERENCED ELEMENTS. Pointing a control at `[its own id, the handle's id]` makes the
     * accessibility layer join the button's own visible verb to the handle, so there is no format
     * string whose word order could be wrong in another locale and no new string in any catalog. Note
     * an element referencing its own id is deliberate and specified: it contributes its own text
     * content.
     *
     * <p>`useId` rather than the username, because the same person can legitimately appear twice on one
     * page (a participant who is also a group leader) and duplicate ids would silently mis-name one of
     * them.
     */
    const idPrefix = React.useId();
    const handleLabelId = `${idPrefix}-handle`;
    const followButtonId = `${idPrefix}-follow`;
    const messageButtonId = `${idPrefix}-message`;

    return (
        <div
            className={cn(
                "flex max-w-full items-center gap-2",
                // Two mechanisms, one decision — see the `align` prop's note. `ml-auto` is what makes
                // the wrapper itself sit right in a `flex w-full` parent, so parameterizing only the
                // `justify-*` would leave the row flush right and change nothing visible.
                align === "start" ? "justify-start" : "ml-auto justify-end",
                allowWrap ? "flex-wrap" : "flex-nowrap",
            )}
            data-playwright-id="user-action-row"
        >


            {/* The referenced half of the composed names below. Never rendered visually — the handle
                is already on the card next to it — and `aria-hidden` is deliberately NOT set: an
                `aria-hidden` node contributes nothing to a name computed through `aria-labelledby`. */}
            <span id={handleLabelId} className="sr-only" translate="no">{`@${username}`}</span>

            {showMessageButton && (
                <CtaButton
                    id={messageButtonId}
                    aria-labelledby={`${messageButtonId} ${handleLabelId}`}
                    icon={MessagesSquare}
                    text={localizer("chat.action.message")}
                    size={"sm"}
                    variant={"ghost"}
                    requiresLogin
                    accountAccessDialog={messagesAccountAccessDialog}
                    onClick={(e) => {
                        e?.preventDefault();
                        e?.stopPropagation();
                        handleOpenChat();
                    }}
                    disabled={anyBusy}
                    playwrightId="user-message-button"
                />
            )}

            {/* Follow is inert on your own card — `toggleFollow` returns early on `isSelf` — so it
                rendered on the followers / following / dashboard lists and did nothing when pressed.
                Suppressed at the RENDER rather than in `mainIsFollow`, so the menu fallback above
                stays off too. */}
            {mainIsFollow && !isSelf && !isEitherDirectionBlocked && (
                <CtaToggle
                    id={followButtonId}
                    aria-labelledby={`${followButtonId} ${handleLabelId}`}
                    icon={isFollowing ? UserMinus : UserPlus}
                    text={isFollowing ? (localizer("user.unfollow")) : (localizer("user.follow"))}
                    buttonActive={isFollowing}
                    activeClass={followActiveClass}
                    extraClass="follow-user-button"
                    interactionEffect="follow"
                    size={"sm"}
                    variant={followVariant}
                    requiresLogin
                    accountAccessDialog={followAccountAccessDialog}
                    onPressedChange={() => toggleFollow()}
                    disabled={anyBusy}
                    playwrightId="user-follow-toggle"
                />
            )}

            {/* GROUP-47 (chat completeness `1h`, 2026-09-27): Ban takes the quiet-destructive `danger`
                tier and sits behind its own divider, the arrangement the same feature's bulk bar
                (`GroupMembersToolbar`) already chose. As `ghost` it was identical in weight to Direct
                message 8 px away, in the right-thumb arc. Unban is not destructive, so it stays ghost. */}
            {mainIsBan && (
                <div className="flex items-center border-s border-border ps-2" data-user-buttons-destructive="true">
                    <CtaToggle
                        icon={isBanned ? UserPlus : UserMinus}
                        text={isBanned ? (localizer("groups.unban")) : (localizer("groups.ban"))}
                        buttonActive={isBanned}
                        size={"sm"}
                        variant={isBanned ? "ghost" : "danger"}
                        requiresLogin
                        accountAccessDialog={accountAreaAccountAccessDialog}
                        onPressedChange={() => (isBanned ? handleUnban() : handleBan())}
                        disabled={anyBusy}
                        playwrightId="group-member-ban-toggle"
                    />
                </div>
            )}

            {mainIsRequest && (
                <>
                    <CtaButton
                        icon={UserPlus}
                        text={localizer("groups.request.approve")}
                        size={"sm"}
                        variant="primary"
                        playwrightId="group-membership-approve"
                        requiresLogin
                        accountAccessDialog={accountAreaAccountAccessDialog}
                        onClick={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            void handleApprove();
                        }}
                        disabled={anyBusy}
                    />
                    <CtaButton
                        icon={UserMinus}
                        text={localizer("groups.request.reject")}
                        size={"sm"}
                        variant="destructive"
                        playwrightId="group-membership-reject"
                        requiresLogin
                        accountAccessDialog={accountAreaAccountAccessDialog}
                        onClick={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            void handleReject();
                        }}
                        disabled={anyBusy}
                    />
                    {/* SV-146: Approve · Decline · Ignore, the quietest last. */}
                    <CtaButton
                        icon={EyeOff}
                        text={localizer("groups.request.ignore")}
                        size={"sm"}
                        variant="ghost"
                        playwrightId="group-membership-ignore"
                        requiresLogin
                        accountAccessDialog={accountAreaAccountAccessDialog}
                        onClick={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            void handleIgnore();
                        }}
                        disabled={anyBusy}
                    />
                </>
            )}

            {displayMode !== "primary-only" && dropdownSections.length > 0 && (
                <CtaButtonDropdown
                    icon={MoreHorizontal}
                    // `DISCUSSION-13`: nine of these on the participants page, each announcing as a
                    // bare "button" while holding report / block / mute.
                    //
                    // `ariaLabel` and not the `aria-labelledby` pair used on the two controls above,
                    // and the difference is not an oversight. Those two have visible text of their own
                    // for the id list to pick up; this one is icon-only, so a self-reference would
                    // contribute nothing. Folding the handle in here too would mean a SECOND sr-only
                    // span holding "More options" for the id list to reference — doable by the same
                    // zero-key pattern, but this half is already closed and a correct-but-generic name
                    // is not a defect. Do NOT "improve" it by appending the handle to the localized
                    // string in code: that is the word-order bug the pattern above exists to avoid.
                    ariaLabel={localizer("typescript.discussion.preview.publish_settings.more_options")}
                    playwrightId="user-more-options-button"
                    variant="ghost"
                    size={"sm"}
                    extraClass="user-more-options-button"
                    dropdownSections={dropdownSections}
                />
            )}
        </div>
    );
}

